// Fenn yapıları ve SubGroup tanımları

export interface FennRange {
  fenn: string;
  start: number;
  end: number;
  subGroup?: string; // RK, Rİ, DT, TC
}

export interface QrupConfig {
  qrup: string;
  fennler: FennRange[];
  hasSubGroup: boolean;
}

// Her qrup için fenn yapılandırması
export const QRUP_CONFIGS: QrupConfig[] = [
  {
    qrup: 'I',
    hasSubGroup: true,
    fennler: [
      { fenn: 'Riyaziyyat', start: 1, end: 30 },
      { fenn: 'Fizika', start: 31, end: 60 },
      { fenn: 'Kimya', start: 61, end: 90, subGroup: 'RK' },
      { fenn: 'İnformatika', start: 61, end: 90, subGroup: 'Rİ' }
    ]
  },
  {
    qrup: 'II',
    hasSubGroup: false,
    fennler: [
      { fenn: 'Riyaziyyat', start: 1, end: 30 },
      { fenn: 'Coğrafiya', start: 31, end: 60 },
      { fenn: 'Tarix', start: 61, end: 90 }
    ]
  },
  {
    qrup: 'III',
    hasSubGroup: true,
    fennler: [
      { fenn: 'Azərbaycan dili', start: 1, end: 30 },
      { fenn: 'Tarix', start: 31, end: 60 },
      { fenn: 'Ədəbiyyat', start: 61, end: 90, subGroup: 'DT' },
      { fenn: 'Coğrafiya', start: 61, end: 90, subGroup: 'TC' }
    ]
  },
  {
    qrup: 'IV',
    hasSubGroup: false,
    fennler: [
      { fenn: 'Biologiya', start: 1, end: 30 },
      { fenn: 'Kimya', start: 31, end: 60 },
      { fenn: 'Fizika', start: 61, end: 90 }
    ]
  }
];

// Tüm fennlerin listesi
export const ALL_FENNLER = [
  'Riyaziyyat',
  'Fizika',
  'Kimya',
  'İnformatika',
  'Coğrafiya',
  'Tarix',
  'Azərbaycan dili',
  'Ədəbiyyat',
  'Biologiya'
];

// SubGroup tanımları
export const SUBGROUPS = {
  RK: { qrup: 'I', fennler: ['Kimya'] },
  'Rİ': { qrup: 'I', fennler: ['İnformatika'] },
  DT: { qrup: 'III', fennler: ['Ədəbiyyat'] },
  TC: { qrup: 'III', fennler: ['Coğrafiya'] }
};

// Sınıflar
export const SINIFLAR = ['8', '9', '10', '11'];

// Qruplar
export const QRUPLAR = ['I', 'II', 'III', 'IV'];

/** Arabic 1–4 and Roman I–IV → canonical Roman string. Empty/unknown → ''. */
export function normalizeQrup(value: unknown): string {
  if (value === null || value === undefined) return '';
  const s = String(value).trim().toUpperCase();
  if (!s || s === '-') return '';
  const map: Record<string, string> = {
    '1': 'I',
    '2': 'II',
    '3': 'III',
    '4': 'IV',
    I: 'I',
    II: 'II',
    III: 'III',
    IV: 'IV',
  };
  return map[s] ?? s;
}

export function qrupEquals(a: unknown, b: unknown): boolean {
  const na = normalizeQrup(a);
  const nb = normalizeQrup(b);
  if (!na || !nb) return false;
  return na === nb;
}

/** Firestore query values for specialtyGroup: Roman string, Arabic string, Arabic number. */
export function getQrupQueryValues(qrup: unknown): Array<string | number> {
  const roman = normalizeQrup(qrup);
  if (!roman) return [];
  const arabicMap: Record<string, number> = { I: 1, II: 2, III: 3, IV: 4 };
  const n = arabicMap[roman];
  if (n === undefined) return [roman];
  return [roman, String(n), n];
}

export function getQrupConfig(qrup: unknown): QrupConfig | undefined {
  const normalized = normalizeQrup(qrup);
  if (!normalized) return undefined;
  return QRUP_CONFIGS.find(q => q.qrup === normalized);
}

// Fenn'e göre hangi qrup ve subgroup olduğunu bulan fonksiyon
export const getFennConfig = (fenn: string): { qrup: string; subGroup?: string }[] => {
  const configs: { qrup: string; subGroup?: string }[] = [];
  
  QRUP_CONFIGS.forEach(qrupConfig => {
    qrupConfig.fennler.forEach(fennRange => {
      if (fennRange.fenn === fenn) {
        configs.push({
          qrup: qrupConfig.qrup,
          subGroup: fennRange.subGroup
        });
      }
    });
  });
  
  return configs;
};

// Qrup'a göre fennleri getiren fonksiyon
export const getFennlerByQrup = (qrup: string): FennRange[] => {
  const qrupConfig = getQrupConfig(qrup);
  return qrupConfig?.fennler || [];
};

// Qrup'un subGroup'u olup olmadığını kontrol eden fonksiyon
export const hasSubGroup = (qrup: string): boolean => {
  const qrupConfig = getQrupConfig(qrup);
  return qrupConfig?.hasSubGroup || false;
};

// Fenn ve Qrup'a göre subGroup'u bulan fonksiyon
export const getSubGroupForFenn = (fenn: string, qrup: string): string | undefined => {
  const qrupConfig = getQrupConfig(qrup);
  const fennRange = qrupConfig?.fennler.find(f => f.fenn === fenn);
  return fennRange?.subGroup;
};

// Yoklama kartı için unique key oluşturan fonksiyon
export const createYoxlamaKey = (sinif: string, qrup: string, subGroup?: string): string => {
  const normalizedQrup = normalizeQrup(qrup) || qrup;
  if (subGroup) {
    return `${sinif}-${normalizedQrup}-${subGroup}`;
  }
  return `${sinif}-${normalizedQrup}`;
};

// Yoklama kartını parse eden fonksiyon
export const parseYoxlamaKey = (key: string): { sinif: string; qrup: string; subGroup?: string } => {
  const parts = key.split('-');
  return {
    sinif: parts[0],
    qrup: normalizeQrup(parts[1]) || parts[1],
    subGroup: parts[2]
  };
};

// Fenn ve soru numarasına göre qrup ve subgroup bulan fonksiyon
export const getQrupByQuestionNumber = (questionNumber: number): { qrup: string; subGroup?: string }[] => {
  const results: { qrup: string; subGroup?: string }[] = [];
  
  QRUP_CONFIGS.forEach(qrupConfig => {
    qrupConfig.fennler.forEach(fennRange => {
      if (questionNumber >= fennRange.start && questionNumber <= fennRange.end) {
        results.push({
          qrup: qrupConfig.qrup,
          subGroup: fennRange.subGroup
        });
      }
    });
  });
  
  return results;
};



